import { Paginator } from "primereact/paginator";
import { useCallback, useEffect, useState } from "react";
import { LoadingService } from "../../helpers/loading";
import { getEvents } from "../../services/eventsService";
import EventCard from "./EventCard";
import KeduLoading from "../loading/KeduLoading";
import NoData from "../nodata/NoData";
import { useUrlParam } from "../../hook/useUrlParam";
import { EVENTS_PAGE } from "../../constants/app-constants";
import { translate } from "../../localization/translateService";

const EVENTS_PER_PAGE = 12;
const EVENT_DATE_LABEL_KEYS = {
    START_DATE: "TITLE.START_DATE",
    END_DATE: "TITLE.END_DATE",
};

export default function EventList({ isExpired, trans }) {
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(EVENTS_PER_PAGE);
    const [total, setTotal] = useState(0);
    const [events, setEvents] = useState(null);
    const [translations, setTranslations] = useState({
        START_DATE: "",
        END_DATE: "",
    });
    const SCROLL_CONFIG = { top: 0, behavior: 'smooth' };

    const { getParam: getPage, setParam: setPage } = useUrlParam(EVENTS_PAGE.FIRST);

    const fetchEvents = useCallback(async () => {
        const requestId = LoadingService.generateId();
        LoadingService.increment("EVENT LIST",requestId);
        try {
            const paging = { first, rows, multiSortMeta: [] };
            const filters = { isExpired: { value: isExpired } };
            const { data, pagingInfo } = (await getEvents(paging, filters)).data;

            setTotal(pagingInfo?.totalRecordCount || 0);
            setEvents(data);
        } finally {
            LoadingService.decrement("EVENT LIST",requestId);
        }
    }, [first, rows, isExpired]);

    useEffect(() => {
        setFirst(0);
        setPage(0);
    }, [isExpired]);

    useEffect(() => {
        fetchEvents();
    }, [first, isExpired, fetchEvents]);

    useEffect(() => {
        const savedPage = getPage();
        if (savedPage) {
            setFirst(savedPage);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadDateLabels = async () => {
            try {
                const [START_DATE, END_DATE] = await Promise.all([
                    translate(EVENT_DATE_LABEL_KEYS.START_DATE, true),
                    translate(EVENT_DATE_LABEL_KEYS.END_DATE, true),
                ]);
                if (!cancelled) {
                    setTranslations({ START_DATE, END_DATE });
                }
            } catch {
                if (!cancelled) {
                    setTranslations({ START_DATE: "", END_DATE: "" });
                }
            }
        };
        loadDateLabels();
        return () => {
            cancelled = true;
        };
    }, []);

    const paginatorTemplate = {
        layout: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        FirstPageLink: (options) => {
            return (
                <button
                    type="button"
                    className={options.className}
                    onClick={options.onClick}
                    disabled={options.disabled}
                >
                    <i className="pi pi-angle-double-left"></i>
                </button>
            );
        },
        PrevPageLink: (options) => {
            return (
                <button
                    type="button"
                    className={options.className}
                    onClick={options.onClick}
                    disabled={options.disabled}
                >
                    <i className="pi pi-angle-left"></i>
                </button>
            );
        },
        PageLinks: (options) => {
            return (
                <button
                    type="button"
                    className={options.className}
                    onClick={options.onClick}
                >
                    {options.page + 1}
                </button>
            );
        },
        NextPageLink: (options) => {
            return (
                <button
                    type="button"
                    className={options.className}
                    onClick={options.onClick}
                    disabled={options.disabled}
                >
                    <i className="pi pi-angle-right"></i>
                </button>
            );
        },
        LastPageLink: (options) => {
            return (
                <button
                    type="button"
                    className={options.className}
                    onClick={options.onClick}
                    disabled={options.disabled}
                >
                    <i className="pi pi-angle-double-right"></i>
                </button>
            );
        },
    };

    const onPageChange = (e) => {
        setFirst(e.first);
        setPage(e.first)
        window.scrollTo(SCROLL_CONFIG);
    };

    return (
        events ?
        <div className="flex flex-column">
            {events.length > 0 ? (
                <div className="grid">
                    {events.map((ev, idx) => (
                        <div key={ev.id} className="col-12 md:col-6 lg:col-4 xl:col-3 p-3">
                            <EventCard
                                data={ev}
                                isExpired={isExpired}
                                trans={{ ...trans, ...translations }}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <NoData message={trans?.noEvent} />
            )}

            {total > rows && (
                <Paginator
                    template={paginatorTemplate}
                    first={first}
                    rows={rows}
                    totalRecords={total}
                    onPageChange={onPageChange}
                    className="keduall-paginator"
                />
            )}
        </div>
        :
        <KeduLoading />
    )
}